import { AlertCircle, CheckCircle2, Instagram, Mail, MessageCircle, MessageSquare, Send, LoaderCircle, Radio } from 'lucide-react';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Channel } from '@/lib/types';
import { channelName, cn, initials } from '@/lib/utils';
export function ChannelIcon({ channel, size = 14, withLabel = false }: {
    channel: Channel;
    size?: number;
    withLabel?: boolean;
}) {
    const Icon = { whatsapp: MessageCircle, instagram: Instagram, email: Mail, telegram: Send, sms: MessageSquare }[channel];
    return <span className={cn('channel-icon', `channel-${channel}`, withLabel && 'channel-with-label')} title={channelName[channel]}><Icon size={size}/>{withLabel && <span>{channelName[channel]}</span>}</span>;
}
export function Avatar({ name, color = 'teal', size = 'md' }: {
    name: string;
    color?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}) { return <span aria-hidden="true" className={cn('avatar', `avatar-${color}`, `avatar-${size}`)}>{initials(name)}</span>; }
export function StatusBadge({ status }: {
    status: string;
}) { const label: Record<string, string> = { active: 'Connected', paused: 'Paused', reauth_required: 'Reconnect needed', not_connected: 'Disconnected', connecting: 'Connecting', error: 'Error' }; return <span className={cn('status-badge', `status-${status}`)}><i />{label[status] ?? status}</span>; }
export function EmptyState({ title, description, children }: {
    title: string;
    description: string;
    children?: ReactNode;
}) { return <div className="empty-state"><span className="empty-icon"><MessageCircle size={28}/></span><h3>{title}</h3><p>{description}</p>{children}</div>; }
export function Loading({ label = 'Loading your workspace…' }: {
    label?: string;
}) { return <div className="loading"><LoaderCircle className="spin" size={20}/><span>{label}</span></div>; }
export function ErrorBanner({ message, onRetry }: {
    message: string;
    onRetry?: () => void;
}) { return <div role="alert" className="error-banner"><AlertCircle size={16}/><span>{message}</span>{onRetry && <button onClick={onRetry}>Retry</button>}</div>; }
export function LiveIndicator({ error = false }: {
    error?: boolean;
}) { return <span className={cn('live-indicator', error && 'offline')}><Radio size={12}/>{error ? 'Reconnecting' : 'Live'}</span>; }
const ToastContext = createContext<(message: string, error?: boolean) => void>(() => { });
export const useToast = () => useContext(ToastContext);
export function ToastProvider({ children }: {
    children: ReactNode;
}) {
    const [toast, setToast] = useState<{
        message: string;
        error: boolean;
        id: number;
    } | null>(null);
    useEffect(() => {
        if (!toast)
            return;
        const timer = setTimeout(() => setToast(null), 4500);
        return () => clearTimeout(timer);
    }, [toast]);
    return <ToastContext.Provider value={(message, error = false) => setToast({ message, error, id: Date.now() })}>{children}{toast && <div className={cn('toast', toast.error && 'toast-error')} role={toast.error ? 'alert' : 'status'}>{toast.error ? <AlertCircle size={18}/> : <CheckCircle2 size={18}/>}<span>{toast.message}</span></div>}</ToastContext.Provider>;
}
